import React, { useCallback, useMemo, useState } from "react";
import { Box, Typography, keyframes } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  IconDesign,
  IconTechPack,
  IconMoodboard,
  IconRecap,
  IconGestion,
  IconProduct,
  IconMeetings,
  IconAiDesigns,
  IconSketches,
  IconSeamless,
  IconTartan,
  IconClientInfo,
} from "./LoginWorkflowIcons";
import moodFlat00 from "../../img/login/mood-flat-00.png";
import moodFlat01 from "../../img/login/mood-flat-01.png";
import moodFlat02 from "../../img/login/mood-flat-02.png";
import moodFlat03 from "../../img/login/mood-flat-03.png";
import moodFlat04 from "../../img/login/mood-flat-04.png";
import {
  STUDIO_ACCENT,
  STUDIO_HAIRLINE,
  STUDIO_INK,
  STUDIO_PAPER,
  STUDIO_SURFACE,
  STUDIO_FONT,
} from "../Sidebar/sidebarStudioTokens";

const MOOD_FLATS = [moodFlat00, moodFlat01, moodFlat02, moodFlat03, moodFlat04];

/** Misma paleta que Sidebar / Mi Dashboard. */
const ACCENT = STUDIO_ACCENT;
const INK = STUDIO_INK;
const HAIRLINE = STUDIO_HAIRLINE;
const SURFACE = STUDIO_SURFACE;
const PAPER = STUDIO_PAPER;
const FONT_LOGO = 'Roboto, "Helvetica Neue", Arial, sans-serif';
const FONT_SANS = STUDIO_FONT;

const MODULES = [
  { id: "design", Icon: IconDesign },
  { id: "meetings", Icon: IconMeetings },
  { id: "recap", Icon: IconRecap },
  { id: "moodboard", Icon: IconMoodboard },
  { id: "aidesigns", Icon: IconAiDesigns },
  { id: "sketches", Icon: IconSketches },
  { id: "techpack", Icon: IconTechPack },
  { id: "seamless", Icon: IconSeamless },
  { id: "tartan", Icon: IconTartan },
  { id: "clientinfo", Icon: IconClientInfo },
  { id: "gestion", Icon: IconGestion },
  { id: "product", Icon: IconProduct },
];

const fadeUp = keyframes`
  from { opacity: 0; transform: translate3d(0, 10px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

/**
 * Barrido L→R: pinta ~3s, pausa corta, siguiente flat.
 * Ciclo ≈ 9s (antes 20s).
 */
const paintSweep = keyframes`
  0%, 3% {
    -webkit-mask-position: 100% 0;
    mask-position: 100% 0;
    opacity: 0;
  }
  6% { opacity: 0.045; }
  32% {
    -webkit-mask-position: 0% 0;
    mask-position: 0% 0;
    opacity: 0.04;
  }
  42% { opacity: 0.022; }
  50%, 100% {
    -webkit-mask-position: 0% 0;
    mask-position: 0% 0;
    opacity: 0;
  }
`;

/** Filamento de pincel que cruza la página (borde del barrido). */
const brushEdge = keyframes`
  0%, 3% { left: -8%; opacity: 0; }
  6% { opacity: 0.14; }
  32% { left: 108%; opacity: 0.08; }
  36%, 100% { left: 108%; opacity: 0; }
`;

function SketchBackdrop() {
  const [flatIndex, setFlatIndex] = useState(0);
  const advanceFlat = useCallback(() => {
    setFlatIndex((i) => (i + 1) % MOOD_FLATS.length);
  }, []);

  const mask = `linear-gradient(
    90deg,
    #000 0%,
    #000 42%,
    rgba(0,0,0,0.55) 52%,
    transparent 62%
  )`;

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        overflow: "hidden",
        pointerEvents: "none",
        "@media (prefers-reduced-motion: reduce)": { display: "none" },
      }}
    >
      {/* Flat rotativo: multiply = solo trazos; cambia en cada ciclo */}
      <Box
        component="img"
        src={MOOD_FLATS[flatIndex]}
        alt=""
        onAnimationIteration={advanceFlat}
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          width: { xs: "140%", sm: "110%", lg: "92%" },
          maxWidth: 1280,
          height: "auto",
          transform: "translate(-50%, -50%)",
          objectFit: "contain",
          mixBlendMode: "multiply",
          filter: "contrast(1.35) brightness(1.08)",
          opacity: 0,
          WebkitMaskImage: mask,
          maskImage: mask,
          WebkitMaskSize: "220% 100%",
          maskSize: "220% 100%",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "100% 0",
          maskPosition: "100% 0",
          animation: `${paintSweep} 9s ease-in-out infinite`,
        }}
      />

      {/* Filo del barrido — acento TON muy suave */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          width: { xs: 48, md: 72 },
          left: "-8%",
          background: `linear-gradient(90deg,
            transparent 0%,
            ${ACCENT}28 45%,
            transparent 100%)`,
          filter: "blur(10px)",
          animation: `${brushEdge} 9s ease-in-out infinite`,
        }}
      />
    </Box>
  );
}

function isoWeekMeta(date = new Date()) {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week };
}

export default function LoginFashionMood() {
  const { t } = useTranslation();
  const meta = useMemo(() => isoWeekMeta(), []);

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        overflow: "hidden",
        pointerEvents: "none",
        display: "flex",
        background: `
          radial-gradient(920px 620px at 16% 32%, ${SURFACE} 0%, transparent 58%),
          radial-gradient(640px 480px at 92% 6%, ${ACCENT}12 0%, transparent 48%),
          ${PAPER}
        `,
      }}
    >
      <SketchBackdrop />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          width: { xs: "100%", lg: "58%" },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          pt: { xs: 2.5, lg: 0 },
          pb: { xs: 2, lg: "6vh" },
          pl: { xs: 2, sm: 3.5, lg: "4.5%" },
          pr: { xs: 2, lg: 2 },
          boxSizing: "border-box",
          animation: `${fadeUp} 0.75s cubic-bezier(0.22,1,0.36,1) both`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 620 }}>
          <Typography
            sx={{
              fontFamily: FONT_SANS,
              fontSize: "0.62rem",
              fontWeight: 700,
              letterSpacing: "0.26em",
              textTransform: "uppercase",
              color: "rgba(37,37,37,0.38)",
              mb: 1.1,
            }}
          >
            {t("Login.moodStudio")}
          </Typography>

          <Typography
            component="p"
            sx={{
              m: 0,
              mb: 1.1,
              fontFamily: FONT_LOGO,
              fontWeight: 900,
              fontSize: { xs: "0.82rem", sm: "0.9rem" },
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: ACCENT,
            }}
          >
            {t("Login.moodManifesto")}
          </Typography>

          <Typography
            component="h2"
            sx={{
              m: 0,
              mb: 1.15,
              fontFamily: FONT_LOGO,
              fontWeight: 500,
              fontSize: { xs: "1.45rem", sm: "1.72rem", lg: "1.95rem" },
              lineHeight: 1.18,
              letterSpacing: "-0.028em",
              color: INK,
              maxWidth: 460,
            }}
          >
            {t("Login.moodClaim")}
          </Typography>

          <Typography
            sx={{
              mb: { xs: 2.2, lg: 2.75 },
              fontFamily: FONT_SANS,
              fontSize: "0.82rem",
              fontWeight: 500,
              lineHeight: 1.5,
              color: "rgba(37,37,37,0.52)",
              maxWidth: 440,
            }}
          >
            {t("Login.moodClaimSub")}
          </Typography>

          {/* Índice de capacidades: quieto, sin glass, al servicio del form */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: { xs: 0.85, sm: 1 },
            }}
          >
            {MODULES.map(({ id, Icon }, i) => (
              <Box
                key={id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  gap: 0.9,
                  px: 1,
                  py: { xs: 1.35, sm: 1.5 },
                  minHeight: { sm: 96 },
                  borderRadius: "14px",
                  background: SURFACE,
                  border: `1px solid ${HAIRLINE}`,
                  boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset",
                  color: INK,
                  animation: `${fadeUp} 0.55s ease both`,
                  animationDelay: `${0.04 + i * 0.025}s`,
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "50%",
                    background: `radial-gradient(circle at 40% 35%, ${ACCENT}18 0%, transparent 70%)`,
                    color: INK,
                  }}
                >
                  <Icon delay={i * 0.2} size={36} />
                </Box>
                <Typography
                  sx={{
                    fontFamily: FONT_SANS,
                    fontWeight: 700,
                    fontSize: { xs: "0.72rem", sm: "0.76rem" },
                    letterSpacing: "-0.01em",
                    lineHeight: 1.25,
                    color: INK,
                    px: 0.25,
                  }}
                >
                  {t(`Login.moodModules.${id}.title`)}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography
            sx={{
              mt: 2.25,
              display: { xs: "none", md: "block" },
              fontFamily: FONT_SANS,
              fontSize: "0.58rem",
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(37,37,37,0.28)",
            }}
          >
            Stage · W{String(meta.week).padStart(2, "0")} · {meta.year}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: "none", lg: "block" },
          flex: 1,
          height: "100%",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          background: {
            xs: `linear-gradient(180deg, transparent 8%, ${PAPER} 55%)`,
            lg: `linear-gradient(90deg,
              transparent 0%,
              transparent 48%,
              ${PAPER}59 62%,
              ${PAPER} 78%)`,
          },
        }}
      />
    </Box>
  );
}
